import React from "react";
import { useTranslation } from "react-i18next";
import { faFileCsv } from "@fortawesome/free-solid-svg-icons/faFileCsv";

import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { Button } from "../../../components/Button";
import { useAppSelector } from "../../../redux/store";
import { reportSliceSelectors } from "../../../redux/report/report.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { downloadCsv } from "../../../utils/csv";
import { ReportDataset, buildReportCsv } from "../utils/buildReportCsv";

const DATASETS: Array<{ dataset: ReportDataset; labelKey: string }> = [
  { dataset: ReportDataset.SUMMARY, labelKey: "reports.export.summary" },
  { dataset: ReportDataset.MEMBERS, labelKey: "reports.export.members" },
  { dataset: ReportDataset.TREND, labelKey: "reports.export.trend" },
  { dataset: ReportDataset.DEAL_VALUE, labelKey: "reports.export.dealValue" },
  { dataset: ReportDataset.SOURCES, labelKey: "reports.export.sources" },
];

/**
 * Exports whatever the current filter produced. The report is already whole in
 * the store, so this needs no round trip: re-running the aggregations server
 * side just to format a handful of rows would be the expensive way to do it.
 */
export const ReportExportButton: React.FC = () => {
  const { t } = useTranslation();

  const report = useAppSelector(reportSliceSelectors.selectReport);
  const loading = useAppSelector(reportSliceSelectors.selectReportLoading);
  const hasReport = useAppSelector(reportSliceSelectors.selectHasReport);

  const onExport = (dataset: ReportDataset) => {
    const built = buildReportCsv(dataset, report, t);

    // A dataset can be legitimately empty for a quiet range; saying so beats
    // handing over a file with nothing but headers in it.
    if (!built) {
      AtiraToast.error(t("reports.export.empty"));
      return;
    }

    downloadCsv(built.filename, built.content);
    AtiraToast.success(t("reports.export.success"));
  };

  return (
    <AtiraDropdown
      items={DATASETS.map(({ dataset, labelKey }) => ({
        key: dataset,
        label: t(labelKey),
        onClick: () => onExport(dataset),
      }))}
      placement="bottomLeft"
      trigger={["click"]}
      destroyOnHidden
      disabled={loading || !hasReport}
    >
      <Button
        icon={faFileCsv}
        title={t("reports.export")}
        disabled={loading || !hasReport}
        backgroundColor="darkMain"
        hoverBackgroundColor="main"
        padding="s"
        margin="none"
      />
    </AtiraDropdown>
  );
};
